import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useGetIdentity, useTranslate } from "ra-core";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ThemeToggle } from "../layout/ThemeToggle";
import { TasksListByDueDate } from "./TasksListByDueDate";

/**
 * TasksList — vista top-level de Tareas estilo P3.
 *
 * Header con título + toggle "Solo mis tareas":
 *  - Admin: toggle OFF por default → ve tareas de todos.
 *  - Non-admin: toggle ON por default → ve solo las suyas.
 *
 * Ambos pueden alternar libre. El filtro se hace server-side vía
 * el filterBySalesId del componente TasksListByDueDate.
 */
export function TasksList() {
  const translate = useTranslate();
  const { identity, isPending: identityPending } = useGetIdentity();
  const isAdmin = (identity as any)?.administrator === true;
  const [onlyMine, setOnlyMine] = useState<boolean | null>(null);

  useEffect(() => {
    if (identityPending) return;
    if (onlyMine === null) setOnlyMine(!isAdmin);
  }, [identityPending, isAdmin, onlyMine]);

  const title = onlyMine ? "Mis tareas" : "Todas las tareas";

  return (
    <div className="flex flex-col min-h-screen">
      <header className="px-7 pt-6 pb-4 flex items-center gap-3.5 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="grid place-items-center"
            style={{
              width: 38,
              height: 38,
              borderRadius: 12,
              background: "var(--musco-panel2)",
              color: "var(--musco-text)",
            }}
          >
            <CheckCircle2 size={18} />
          </div>
          <div>
            <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
              {title}
            </h1>
            <div className="text-sm" style={{ color: "var(--musco-muted)" }}>
              Pendientes agrupadas por fecha de vencimiento
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: "var(--musco-panel)",
              boxShadow: "var(--musco-shadow)",
            }}
          >
            <Switch
              id="tasks-only-mine"
              checked={!!onlyMine}
              onCheckedChange={(v) => setOnlyMine(v)}
              disabled={identityPending}
            />
            <Label
              htmlFor="tasks-only-mine"
              className="cursor-pointer text-sm whitespace-nowrap"
            >
              Solo mis tareas
            </Label>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 overflow-auto px-7 pb-7">
        <div className="musco-panel p-6">
          {onlyMine === null ? (
            <p className="text-sm" style={{ color: "var(--musco-muted)" }}>
              Cargando…
            </p>
          ) : (
            <div className="flex flex-col gap-4">
              <TasksListByDueDate
                filterBySalesId={onlyMine ? identity?.id : null}
                emptyPlaceholder={
                  <p className="text-sm">
                    {translate("resources.tasks.empty_list_hint")}
                  </p>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
